import type { Chapter } from "@repo/app-types/entities/course";
import type { ProjectWithJobType } from "@repo/app-types/entities/project";
import { Size } from "@repo/ui/helpers/helpers";
import { Link } from "@repo/ui/Link/routerLink";
import { pillStyle } from "@repo/ui/Pills/pill";
import { TableSkeletonContent } from "@repo/ui/Skeletons/skeletons";
import { entityTable } from "@repo/ui/tables/entityTable";
import { TooltipTrigger } from "@repo/ui/tooltip/tooltipTrigger";
import { IconChevronRight } from "@tabler/icons-react";
import { type FC, useCallback } from "react";
import { Button, Heading } from "react-aria-components";
import { useHref, useNavigate } from "react-router";
import { useAllTrainingChapterAssessmentsForProjectQuery } from "../../api/entities/trainingChapterAssessment.js";
import { SizedContainer } from "../../components/sizedContainer.js";
import { renderAssessableParticipantCount } from "../trainingInsights/trainingInsightsHelpers.js";
import { ChapterTooltip } from "./chapterTooltip.js";
import { ChapterTooltipScoreLine } from "./chapterTooltipScoreLine.js";
import { TrainingAssessmentTemplateScorePill } from "./trainingAssessmentTemplateScorePill.js";

export interface ParticipantsAssessmentSectionProps {
  project: ProjectWithJobType;
  onChapterClick: (chapter: Chapter) => void;
}

const tableStyle = entityTable();

export const ChapterAssessmentsSection: FC<
  ParticipantsAssessmentSectionProps
> = (props) => {
  const { data, isLoading } = useAllTrainingChapterAssessmentsForProjectQuery({
    projectId: props.project.id,
  });

  const course = data?.course;
  const assessableParticipantsCount = data?.assessableParticipantsCount;
  const assessments = data?.trainingChapterAssessments;

  // react-aria's Link and react-router's Link do not play nice, so we handle it ourselves
  const navigate = useNavigate();
  const insightsLinkHref = useHref("../insights");
  const handleLinkPress = useCallback(() => {
    navigate(insightsLinkHref);
  }, [insightsLinkHref, navigate]);

  const renderContent = () => {
    if (!data && isLoading) {
      return (
        <tr className={tableStyle.row()}>
          <td colSpan={100} className={tableStyle.empty()}>
            <TableSkeletonContent rowCount={2} />
          </td>
        </tr>
      );
    }

    const chapters = course?.chapters ?? [];

    return (
      <>
        <tr className={tableStyle.row()}>
          <td className={tableStyle.cell()}>
            <div className="flex">
              <div className="w-40 flex-auto truncate font-medium">
                Participation rate
              </div>
            </div>
          </td>

          {chapters.map((chapter) => {
            const assessment = assessments?.find(
              (a) => a.chapter_id === chapter.id,
            );
            const completedParticipantsCount =
              assessment?.score_details.completed_participants_count;
            const participationRate =
              completedParticipantsCount !== undefined &&
              assessableParticipantsCount !== undefined
                ? assessableParticipantsCount !== 0
                  ? completedParticipantsCount / assessableParticipantsCount
                  : null
                : undefined;

            return (
              <td key={chapter.id} className={tableStyle.cell()}>
                <div
                  className={pillStyle({
                    size: Size.SMALL,
                    className: "group ring-transparent",
                  })}
                >
                  <SizedContainer
                    className="text-center tabular-nums"
                    childrenForSize="100%"
                  >
                    {participationRate === undefined ? "--" : null}
                    {participationRate === null ? "n/a" : null}
                    {participationRate !== undefined &&
                    participationRate !== null
                      ? (participationRate * 100).toFixed() + "%"
                      : null}
                  </SizedContainer>
                </div>
              </td>
            );
          })}
        </tr>

        <tr className={tableStyle.row()}>
          <td className={tableStyle.cell({ isLastRow: true })}>
            <div className="flex">
              <Link
                href={insightsLinkHref}
                onClick={handleLinkPress}
                className="group flex w-40 flex-auto items-center gap-1 font-medium hover:text-blue-600"
              >
                <span className="truncate">Playbook preparedness</span>
                <IconChevronRight className="size-4 shrink-0 text-gray-400 group-hover:text-blue-600" />
              </Link>
            </div>
          </td>

          {chapters.map((chapter, chapterIndex) => {
            const assessment = assessments?.find(
              (a) => a.chapter_id === chapter.id,
            );
            const passRate = assessment?.score_details.average_pass_rate;

            return (
              <td
                key={chapter.id}
                className={tableStyle.cell({ isLastRow: true })}
              >
                <TooltipTrigger closeDelay={150}>
                  <Button onPress={() => props.onChapterClick(chapter)}>
                    <TrainingAssessmentTemplateScorePill
                      size={Size.SMALL}
                      score={passRate}
                    />
                  </Button>

                  <ChapterTooltip chapterIndex={chapterIndex} chapter={chapter}>
                    <ChapterTooltipScoreLine
                      label="Average score"
                      passRate={passRate}
                    />
                  </ChapterTooltip>
                </TooltipTrigger>
              </td>
            );
          })}
        </tr>
      </>
    );
  };

  return (
    <section className="space-y-4">
      <Heading className="flex items-baseline gap-2">
        <span className="text-base font-semibold text-foreground">Summary</span>
        {data ? (
          <span className="text-xs text-muted-foreground">
            {renderAssessableParticipantCount(data.assessableParticipantsCount)}
          </span>
        ) : null}
      </Heading>

      <table className={tableStyle.table()}>
        <thead className={tableStyle.header()}>
          <tr>
            <th className={tableStyle.column()}>Metric</th>

            {course?.chapters.map((chapter, chapterIndex) => (
              <th
                key={chapter.id}
                className={tableStyle.column({
                  // Set a fixed width so excess width will go to the most left-hand column
                  className: "w-28",
                })}
              >
                Chapter {chapterIndex + 1}
              </th>
            ))}
          </tr>
        </thead>

        <tbody className={tableStyle.body()}>{renderContent()}</tbody>
      </table>
    </section>
  );
};
